import { useState } from "react";
import { SCIMDialog } from "../../../components/SSOForm/SCIM/SCIMDialog";
import { useUserContext } from "../../../../providers/UserProvider";
import { useOrganizationIdp } from "../../../../hooks/useOrganizationIdp/useOrganizationIdp";
import { LoadingIndicator } from "../../../components/LoadingIndicator/LoadingIndicator";
import { Typography } from "@optimizely/axiom";
import { IdentityProviderDropdown } from "../../../components/SSOForm/SCIM/IdentityProviderDropdown";
import { IdentityProvider, isScimEnabled } from "../../../../domain/IdentityProvider";
import { SCIMDisclosure } from "../../../components/SSOForm/SCIM/SCIMDisclosure";

export const SCIM = () => {
    const { organizationId } = useUserContext();
    const { identityProviders, enableScimForIdentityProvider, isLoadingIdp } = useOrganizationIdp(organizationId);

    const [domainsIdp, setDomainsIdp] = useState<IdentityProvider | null>(null);

    if (isLoadingIdp) return <LoadingIndicator height="100%" type="spinner" />;

    const idpsWithScimApiKey = identityProviders?.filter((idp) => isScimEnabled(idp));
    const idpsWithoutScimApiKey = identityProviders?.filter((idp) => !isScimEnabled(idp));

    return (
        <div className="flex flex--column flex-align--start push-double content-container">
            <>
                <Typography type="header1">System for Cross-domain Identity Management (SCIM)</Typography>
                <Typography tag="p" type="caption">
                    Learn more about SCIM and how it helps you manage user identities efficiently. For detailed
                    information, visit&nbsp;
                    <a
                        href="https://support.optimizely.com/hc/en-us/sections/24970605673869-SCIM-provisioning"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        SCIM Documentation
                    </a>
                    .
                </Typography>
                <Typography tag="p" type="caption"></Typography>
                <IdentityProviderDropdown
                    identityProviders={idpsWithoutScimApiKey || []}
                    onChange={({ identityProvider }) => setDomainsIdp(identityProvider)}
                    disabled={(idpsWithScimApiKey?.length ?? 0) > 0}
                />
                {!identityProviders?.length && (
                    <Typography type="caption" className="push--top">
                        SSO needs to be connected before adding SSO Domains.
                    </Typography>
                )}
                {!!idpsWithScimApiKey?.length && (
                    <Typography type="header4" className="push-double--ends">
                        Existing SCIM Connections
                    </Typography>
                )}
                {idpsWithScimApiKey?.map((idp) => {
                    return <SCIMDisclosure identityProvider={idp} key={idp.id} />;
                })}
                {!!domainsIdp && (
                    <SCIMDialog
                        identityProvider={domainsIdp}
                        onClose={() => setDomainsIdp(null)}
                        onSave={async () => await enableScimForIdentityProvider({ identityProviderId: domainsIdp.id })}
                    />
                )}
            </>
        </div>
    );
};

SCIM.displayName = "SCIMPage";
